import { useCallback, useMemo } from "react";
import { usePaymentMethodsQuery } from "./usePaymentMethodsQuery";
/**
 * Wrapper hook that provides payment data with convenient derived values.
 * This hook mimics the interface that components expect from BillingInformationContext,
 * making migration easier while providing better performance through SWR caching.
 *
 * Data provided:
 * - customerCreditCards: Array of payment methods (cards)
 * - primaryCreditCard: The payment method marked as primary
 * - customerGiftCards: Array of gift cards
 * - enableGiftCards: Whether gift card feature is enabled
 * - isLoading: Loading state
 * - fetchError: Error state
 *
 * @example
 * ```typescript
 * const {
 *   customerCreditCards,
 *   primaryCreditCard,
 *   enableGiftCards,
 *   isLoading
 * } = usePaymentMethods();
 *
 * if (isLoading) return <Spinner />;
 * if (!primaryCreditCard) return <AddCardPrompt />;
 *
 * return <PaymentMethodDisplay card={primaryCreditCard} />;
 * ```
 */
export const usePaymentMethods = () => {
    var _a;
    const { data, fetchError, isLoading, mutate } = usePaymentMethodsQuery();
    // Memoize derived values to prevent unnecessary re-renders
    const customerCreditCards = useMemo(() => (data === null || data === void 0 ? void 0 : data.paymentMethods) || [], [data === null || data === void 0 ? void 0 : data.paymentMethods]);
    const primaryCreditCard = useMemo(() => customerCreditCards.find((card) => card.isPrimary), [customerCreditCards]);
    const customerGiftCards = useMemo(() => (data === null || data === void 0 ? void 0 : data.giftCards) || [], [data === null || data === void 0 ? void 0 : data.giftCards]);
    const enableGiftCards = (_a = data === null || data === void 0 ? void 0 : data.enableGiftCards) !== null && _a !== void 0 ? _a : false;
    /**
     * Force revalidation of payment methods from the server.
     * Use after operations that may have changed payment data outside of SWR mutations.
     */
    const revalidatePaymentMethods = useCallback(() => mutate(), [mutate]);
    /**
     * Clear the payment methods cache and optionally refetch.
     * @param refetch - If true (default), refetches data after clearing. If false, just clears cache.
     */
    const clearPaymentCache = useCallback((refetch = true) => mutate(undefined, { revalidate: refetch }), [mutate]);
    return {
        // Payment method data
        customerCreditCards,
        primaryCreditCard,
        // Gift card data
        customerGiftCards,
        enableGiftCards,
        // SWR states
        isLoading,
        fetchError,
        // Cache control methods
        revalidatePaymentMethods,
        clearPaymentCache,
    };
};
